import { h } from 'preact';
import Store from 'store/store';

import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { joinClasses } from 'utils/utils';
import { canUser } from 'utils/roles';

import HubPageEditor from 'components/editor/pages/hubPageEditor';
import HubNavigation from 'components/editor/hubNavigation/hubNavigation';
import HubFooter from 'components/editor/hubFooter/hubFooter';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import RichTextField from 'components/editor/settings/richTextField';
import HTML from 'components/core/html/html';
import MediaField from 'components/editor/settings/mediaField';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';
import LogoVideo from 'components/editor/pages/about/timeline/logoVideo';
import Button from 'components/button/button';

import s from 'components/editor/pages/about/timeline/timeline.sss';

export default class AboutTimelineEditor extends HubPageEditor {
	constructor(props) {
		super(props);

		this.addTimelineSection = this.addTimelineSection.bind(this);
		this.addSectionEvent = this.addSectionEvent.bind(this);
		this.onDeleteEventClick = this.onDeleteEventClick.bind(this);
		this.onDeleteSectionClick = this.onDeleteSectionClick.bind(this);
		this.deleteSection = this.deleteSection.bind(this);
		this.deleteEvent = this.deleteEvent.bind(this);
		this.logoRef = this.logoRef.bind(this);
		this.onTimelineSectionSort = this.onTimelineSectionSort.bind(this);
		this.onEventSort = this.onEventSort.bind(this);
	}

	logoRef(el) {
		this.logoElement = el;
	}

	addTimelineSection() {
		const parent = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content`);
		const sections = parent?.sections?.toJS() || [];
		const newLength = sections.push({
			video: '',
			poster: '',
			events: [],
		});
		parent.set('sections', sections);
		this.props.select(`/sections/${newLength - 1}`);
	}

	addSectionEvent(e) {
		const sectionIndex = e.target.closest('[data-section-index]')?.dataset.sectionIndex;
		if (!sectionIndex) return;
		const parent = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content/sections/${sectionIndex}`);
		if (!parent) return;

		const events = parent?.events?.toJS() || [];
		const year = new Date().getFullYear().toString();
		const newLength = events.push({ year, mediaAlt: `${year} Timeline Image` });
		parent.set('events', events);
		this.props.select(`/sections/${sectionIndex}/events/${newLength - 1}`);
	}

	async deleteSection() {
		const contentPath = `${this.props.dataRoot || ''}/content`;
		const sections = jsonPointer.get(Store.get().data, contentPath)?.sections?.toJS() || [];

		const sectionMatch = this.props.activeElement.match(/^\/sections\/(\d+)/);
		const sectionIndex = sectionMatch?.[1];

		if (sectionIndex == null || !sections[sectionIndex]) return;

		sections.splice(sectionIndex, 1);
		jsonPointer.get(Store.get().data, contentPath).set('sections', sections);

		// Navigate to section list
		this.props.select('/');
	}


	async deleteEvent() {
		const match = this.props.activeElement.match(/^\/sections\/(\d+)\/events\/(\d+)/);
		if (!match) return;

		const [, sectionIndex, eventIndex] = match;
		const basePath = `${this.props.dataRoot || ''}/content/sections/${sectionIndex}`;
		const events = jsonPointer.get(Store.get().data, basePath)?.events?.toJS() || [];

		if (!events[eventIndex]) return;

		events.splice(eventIndex, 1);
		jsonPointer.get(Store.get().data, basePath).set('events', events);

		// Navigate to the section editor
		this.props.select(`/sections/${sectionIndex}`);
	}

	async onDeleteEventClick() {
		const ok = await confirm('This will delete the selected Event item and cannot be undone', { title: 'Delete Event item?', danger: true, button: 'Delete item' });
		if (!ok) return;
		this.deleteEvent();
	}

	async onDeleteSectionClick() {
		const ok = await confirm('This will delete the selected Section item and cannot be undone', { title: 'Delete Section item?', danger: true, button: 'Delete item' });
		if (!ok) return;
		this.deleteSection();
	}

	onTimelineSectionSort(e) {
		const parent = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content`);
		if (!parent) return;

		const arr = parent.sections?.toJS() || [];
		arr.splice(e.to, 0, arr.splice(e.from, 1)[0]);
		parent.set('sections', arr);
	}

	onEventSort(e) {
		const sectionIndex = e.target.closest('[data-section-index]')?.dataset.sectionIndex;
		if (!sectionIndex) return;
		const parent = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content/sections/${sectionIndex}`);
		if (!parent) return;

		const arr = parent.events?.toJS() || [];
		arr.splice(e.to, 0, arr.splice(e.from, 1)[0]);
		parent.set('events', arr);
	}

	getSectionTitle(section) {
		const events = section?.events?.filter(e => e?.year);
		if (!events?.length) return '';
		const from = events.at(0).year || '';
		const to = events.at(-1).year || '';
		return from === to ? from : `${from}–${to}`;
	}

	renderVisual() {
		const content = this.props.content;
		const intro = content?.intro || {};
		const sections = content?.sections || [];
		const pageName = this.getPageName('about', 'About');

		return (
			<div class={s.visual}>
				<HubNavigation />
				<div class={s.pageHeader}>
					<div class={s.breadcrumbs}>{this.getPageName('landing', 'Home')} / <span class={s.active}>{pageName}</span></div>
					<div class={s.pageTitle} onClick={() => this.props.select(`/`)}>{pageName}</div>
				</div>
				<SelectableSection {...this.props} section="/" class={s.wrapper}>
					<SelectableSection {...this.props} section="/intro" class={s.intro}>
						<h3>{intro?.title}</h3>
						<HTML content={intro?.content} />
					</SelectableSection>
					<div class={joinClasses(s.sections, s.sectionsWrapper)}>
						{sections.map((section, sectionIndex) => (
							<SelectableSection
								{...this.props}
								section={`/sections/${sectionIndex}`}
								class={s.section}
								key={sectionIndex}
							>
								<div>
									{!!section.video && <LogoVideo file={section.video} poster={section.poster} ref={this.logoRef} />}
									{!!section && <div class={s.sectionTitle}>{this.getSectionTitle(section)}</div>}
								</div>
								<div class={s.events}>
									{(section.events || []).map((event, idx) => (
										<SelectableSection
											{...this.props}
											section={`/sections/${sectionIndex}/events/${idx}`}
											class={s.event}
											key={idx}
										>
											<div class={s.details}>
												<div class={s.eventWrapper}>
													<div class={s.eventContent}>
														<div class={s.year}>{event.year}</div>
														<HTML class={s.title} content={event.title} />
														<HTML class={s.description} content={event.description} />
													</div>
													<div class={s.picture}>
														{!!event.media && <img src={event.media} alt={event.mediaAlt} loading="lazy" />}
													</div>
												</div>
											</div>
										</SelectableSection>
									))}
								</div>
							</SelectableSection>
						))}
					</div>
				</SelectableSection>
				<HubFooter />
			</div>
		);
	}

	renderIntroEditor() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		let basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;

		return (
			<div class={s.introEditor}>
				<SimpleField dark label="Title" path={`${basePath}/title`} />
				<RichTextField dark label="Content" path={`${basePath}/content`} />
			</div>
		);
	}

	renderSectionEditor() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) return;

		const basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;
		const content = this.props.content;
		const activeElement = this.props.activeElement;

		const sectionMatch = activeElement?.match(/^\/sections\/(\d+)/);
		const selectedSectionIndex = sectionMatch?.[1];

		if (selectedSectionIndex == null || !content.sections?.[selectedSectionIndex]) {
			return <div class={s.sectionsEditor}>Select a section to view its events.</div>;
		}

		const section = content.sections[selectedSectionIndex];
		// const sortedEvents = [...(section.events || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
		const readonly = !canUser('write');

		return (
			<div class={s.sectionList} data-section-index={selectedSectionIndex}>
				<div class={s.sectionHeader}>
					{/* <SimpleField dark label="Timeline Section" path={`${basePath}/title`} /> */}
					<MediaField
						dark
						label="Section video"
						path={`${basePath}/video`}
						group={this.props.config?.id}
						video
						assetData={{ type: 'About Page Video', aspectRatio: '16:9', recommendedDimensions: '1920:1080' }}
					/>
					<MediaField
						dark
						label="Section video (static frame)"
						path={`${basePath}/poster`}
						group={this.props.config?.id}
						assetData={{ type: 'About Page Image', aspectRatio: '16:9', recommendedDimensions: '1920:1080' }}
					/>
				</div>

				<div class={s.sectionList}>
					<span class={s.groupName}>Timeline entries</span>
					<Sortable class={s.eventList} onChange={this.onEventSort} disabled={readonly}>
						{section?.events?.map((event, idx) => (
							<SortableItem key={idx} class={joinClasses(s.sortableItemWrapper, s.eventListItem)} data-event-index={idx}>
								<Handle class={s.handle} />
								<div class={s.sectionListItem} data-event={idx}>
									{event.year || `Event ${idx + 1}`}
									<Button primary class={s.edit} onClick={() => this.props.select(`/sections/${selectedSectionIndex}/events/${idx}`)}>
										Edit
									</Button>
								</div>
							</SortableItem>
						))}
						<Button primary icon="plus" class={s.add} onClick={this.addSectionEvent} disabled={readonly}>
							Add event
						</Button>
					</Sortable>
				</div>
				<Button danger onClick={this.onDeleteSectionClick} disabled={readonly}>Delete item</Button>
			</div>
		);
	}

	renderEventEditor() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		let basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;
		const readonly = !canUser('write');

		return (
			<div class={s.sectionsEditor}>
				<MediaField
					dark
					label="Asset"
					path={`${basePath}/media`}
					group={this.props.config?.id}
					assetData={{ type: 'About Page Image', aspectRatio: '1:1', recommendedDimensions: '400:400' }}
				/>
				<SimpleField dark label="Year" path={`${basePath}/year`} />
				<SimpleField dark label="Title" path={`${basePath}/title`} />
				<RichTextField dark label="Text" path={`${basePath}/description`} />
				<SimpleField dark label="Asset Alt text" path={`${basePath}/mediaAlt`} />
				<Button danger onClick={this.onDeleteEventClick} disabled={readonly}>Delete item</Button>
			</div>
		);
	}

	renderAboutEditor() {
		const content = this.props.content;
		const sections = content?.sections || [];
		const readonly = !canUser('write');

		return (
			<div class={s.sectionList}>
				<div class={s.sectionListItem}>
					Intro
					<Button primary class={s.edit} onClick={() => this.props.select(`/intro`)}>Edit</Button>
				</div>
				<div class={s.sectionList} data-timeline-sections>
					<span>Timeline sections</span>
					<Sortable onChange={this.onTimelineSectionSort} class={s.sectionList} disabled={readonly}>
						{sections.map((section, index) => (
							<SortableItem key={index} class={s.sortableItemWrapper} data-section-index={index}>
								<Handle class={s.handle} />
								<div class={s.sectionListItem}>
									<span class={s.sectionTitle}>{this.getSectionTitle(section)}</span>
									<Button primary class={s.edit} onClick={() => this.props.select(`/sections/${index}`)}>Edit</Button>
								</div>
							</SortableItem>
						))}
						<Button primary icon="plus" class={s.add} onClick={this.addTimelineSection} disabled={readonly}>
							Add section
						</Button>
					</Sortable>
				</div>
			</div>
		);
	}

	renderSettings() {
		let settingsContent;
		const content = this.props.content;
		const nav = [{ label: 'Timeline', path: '/' }];
		const activeElement = this.props.activeElement;

		if (activeElement === '/') {
			settingsContent = this.renderAboutEditor();
		} else if (activeElement?.startsWith('/intro')) {
			nav.push({ label: 'Intro', path: activeElement });
			settingsContent = this.renderIntroEditor();
		} else if (/^\/sections\/([^/]+)\/events\/(\d+)$/.test(activeElement)) {
			const [, sectionId, eventIdx] = activeElement.match(/^\/sections\/([^/]+)\/events\/(\d+)$/);
			const section = content?.sections?.[sectionId];
			const event = section?.events?.[eventIdx];

			nav.push(
				{ label: this.getSectionTitle(section) || 'Section', path: `/sections/${sectionId}` },
				{ label: event?.year || event?.title || 'Event', path: activeElement }
			);

			settingsContent = this.renderEventEditor();
		} else if (/^\/sections\/([^/]+)$/.test(activeElement)) {
			const [, sectionId] = activeElement.match(/^\/sections\/([^/]+)$/);
			const section = content?.sections?.[sectionId];

			nav.push({ label: this.getSectionTitle(section) || 'Section', path: activeElement });
			settingsContent = this.renderSectionEditor();
		}
		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{settingsContent}
			</div>
		);
	}
}